import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, Check, CreditCard, Loader2, ShoppingBag, Download } from "lucide-react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import { VideoPresentation } from "@/components/VideoPresentation";
import { Stars } from "@/components/Stars";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LangContext";
import { api, formatApiError } from "@/lib/api";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(false);
  const [buying, setBuying] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [rf, setRf] = useState({ name: "", rating: 5, comment: "", email: "" });
  const [rSubmitting, setRSubmitting] = useState(false);
  const [related, setRelated] = useState([]);
  const { add } = useCart();
  const { t } = useLang();
  const [qf, setQf] = useState({ name: "", email: "", phone: "", company: "", qty: 1, message: "" });
  const [qSubmitting, setQSubmitting] = useState(false);
  const [qSent, setQSent] = useState(false);

  const submitExpressQuote = async (e) => {
    e.preventDefault();
    setQSubmitting(true);
    try {
      await api.post("/quotes", {
        name: qf.name, email: qf.email, phone: qf.phone, company: qf.company,
        subject: `Devis express : ${product.name} (x${qf.qty})`,
        message: `Demande de devis express depuis la fiche produit.\nProduit : ${product.name}\nQuantité : ${qf.qty}\n${qf.message}`.trim(),
      });
      setQSent(true);
      toast.success("Demande envoyée ! Réponse sous 48h ouvrées.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setQSubmitting(false);
    }
  };

  const loadProduct = () => api.get(`/products/${id}`).then((r) => {
    setProduct(r.data);
    api.get("/products").then((res) => {
      const all = res.data.filter((p) => p.id !== r.data.id);
      const sameSub = all.filter((p) => p.subcategory && p.subcategory === r.data.subcategory);
      const sameCat = all.filter((p) => p.category === r.data.category && !sameSub.includes(p));
      setRelated([...sameSub, ...sameCat].slice(0, 3));
    }).catch(() => {});
  }).catch(() => setError(true));
  const loadReviews = () => api.get(`/products/${id}/reviews`).then((r) => setReviews(r.data)).catch(() => {});

  useEffect(() => {
    window.scrollTo(0, 0);
    loadProduct();
    loadReviews();
  }, [id]);

  const submitReview = async (e) => {
    e.preventDefault();
    setRSubmitting(true);
    try {
      await api.post(`/products/${id}/reviews`, rf);
      toast.success("Merci pour votre avis !");
      setRf({ name: "", rating: 5, comment: "", email: "" });
      await loadProduct();
      await loadReviews();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setRSubmitting(false);
    }
  };

  const buy = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/payments/checkout", { product_id: id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch {
      setBuying(false);
    }
  };

  if (error) return (
    <div className="pt-40 pb-40 text-center" data-testid="product-not-found">
      <p className="font-display text-3xl mb-6">Produit introuvable</p>
      <Link to="/catalogue" className="btn-primary px-6 py-3 font-semibold">Retour au catalogue</Link>
    </div>
  );

  if (!product) return <div className="pt-[148px] min-h-screen" />;

  const features = [
    product.category === "epi" ? "Conforme aux normes CE en vigueur" : "Réglages ergonomiques multiples",
    "Livraison en entreprise sous 5 à 10 jours",
    "Devis volume & tarifs professionnels",
    "Garantie et SAV assurés par ALDOL-SOLUTIONS",
  ];

  return (
    <div className="pt-[148px]" data-testid="product-detail-page">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-10">
        <Link to="/catalogue" className="link-hover inline-flex items-center gap-2 text-sm font-medium" data-testid="back-catalogue">
          <ArrowLeft size={16} /> Retour au catalogue
        </Link>
      </div>
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-16 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        <div className="overflow-hidden self-start" style={{ border: "1px solid var(--border)" }}>
          <motion.img initial={{ scale: 1.1, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            src={product.image} alt={product.name} className="w-full h-[380px] lg:h-[560px] object-cover" />
        </div>
        <div>
          <Reveal>
            <span className="overline" style={{ color: "var(--orange)" }}>{product.category === "epi" ? "Équipement EPI" : "Matériel ergonomique"}</span>
            {product.badge && <span className="ml-3 overline px-3 py-1" style={{ background: "var(--orange)", color: "var(--bone)" }}>{product.badge}</span>}
            <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-none mt-5">{product.name}</h1>
            {product.rating_count > 0 && (
              <div className="flex items-center gap-2 mt-4" data-testid="detail-rating">
                <Stars value={product.rating_avg} size={18} />
                <span className="text-sm" style={{ color: "var(--ink-2)" }}>{product.rating_avg.toFixed(1)} · {product.rating_count} avis</span>
              </div>
            )}
            <p className="mt-4 text-lg" style={{ color: "var(--ink-2)" }}>{product.short}</p>
            <p className="mt-8 font-display font-black text-3xl" data-testid="product-price-quote">Tarif sur devis</p>
            <p className="mt-1 text-sm" style={{ color: "var(--ink-2)" }}>Devis personnalisé gratuit sous 48h, adapté à vos volumes et à votre pays</p>
            <VideoPresentation endpoint={`/products/${product.id}`} />
            <p className="mt-3 inline-flex items-center gap-2 text-sm font-semibold px-3 py-1.5" data-testid="stock-badge"
              style={product.stock_qty === 0
                ? { border: "1px solid #B3261E", color: "#B3261E" }
                : product.stock === "sur_commande"
                  ? { border: "1px solid var(--orange)", color: "var(--orange)" }
                  : { border: "1px solid var(--border)", color: "var(--ink)" }}>
              <span className="w-2 h-2 rounded-full" style={{ background: product.stock_qty === 0 ? "#B3261E" : product.stock === "sur_commande" ? "var(--orange)" : "#3E8E5A" }} />
              {product.stock_qty === 0
                ? "Rupture de stock — réapprovisionnement en cours"
                : product.stock === "sur_commande"
                  ? "Sur commande — délai 2 à 4 semaines"
                  : product.stock_qty != null && product.stock_qty <= 5
                    ? `Plus que ${product.stock_qty} en stock — expédition sous 48h`
                    : product.stock_qty != null
                      ? `En stock (${product.stock_qty} unités) — expédition sous 48h`
                      : "En stock — expédition sous 48h"}
            </p>
            {product.description && <p className="mt-8 text-lg leading-relaxed" style={{ color: "var(--ink-2)" }}>{product.description}</p>}
            <ul className="mt-8 space-y-3">
              {features.map((f) => (
                <li key={f} className="flex items-center gap-3"><Check size={18} style={{ color: "var(--orange)" }} /> {f}</li>
              ))}
            </ul>
            <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4">
              <Link to={`/contact?produit=${encodeURIComponent(product.name)}`} data-testid="detail-devis-btn" className="btn-outline px-8 py-4 font-semibold text-center">
                Demande détaillée via le formulaire complet
              </Link>
            </div>

            <div className="mt-8 p-6" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid="express-quote-block">
              <h3 className="font-display font-black text-xl">{t("Devis express", "Express quote")}</h3>
              <p className="text-sm mt-1 mb-5" style={{ color: "var(--ink-2)" }}>{t("Recevez votre tarif personnalisé sous 48h, sans quitter cette page.", "Get your personalised price within 48h, without leaving this page.")}</p>
              {qSent ? (
                <p className="font-semibold flex items-center gap-2" style={{ color: "#1E8A3C" }} data-testid="express-quote-success">
                  <Check size={18} /> {t("Demande bien reçue ! Nous revenons vers vous sous 48h ouvrées.", "Request received! We'll get back to you within 48 business hours.")}
                </p>
              ) : (
                <form onSubmit={submitExpressQuote} className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="express-quote-form">
                  <input className="field" required placeholder={t("Nom complet *", "Full name *")} value={qf.name} onChange={(e) => setQf({ ...qf, name: e.target.value })} data-testid="express-quote-name" />
                  <input className="field" type="email" required placeholder="Email *" value={qf.email} onChange={(e) => setQf({ ...qf, email: e.target.value })} data-testid="express-quote-email" />
                  <input className="field" placeholder={t("Téléphone", "Phone")} value={qf.phone} onChange={(e) => setQf({ ...qf, phone: e.target.value })} data-testid="express-quote-phone" />
                  <input className="field" placeholder={t("Entreprise / Institution", "Company / Institution")} value={qf.company} onChange={(e) => setQf({ ...qf, company: e.target.value })} data-testid="express-quote-company" />
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-semibold shrink-0" htmlFor="express-qty">{t("Quantité :", "Quantity:")}</label>
                    <input id="express-qty" className="field" type="number" min="1" max="9999" value={qf.qty} onChange={(e) => setQf({ ...qf, qty: e.target.value })} data-testid="express-quote-qty" />
                  </div>
                  <input className="field" placeholder={t("Précisions (optionnel)", "Details (optional)")} value={qf.message} onChange={(e) => setQf({ ...qf, message: e.target.value })} data-testid="express-quote-message" />
                  <button type="submit" disabled={qSubmitting} className="btn-primary sm:col-span-2 py-3.5 font-semibold flex items-center justify-center gap-2" data-testid="express-quote-submit">
                    {qSubmitting ? <Loader2 size={17} className="animate-spin" /> : t("Recevoir mon devis sous 48h", "Get my quote within 48h")}
                  </button>
                </form>
              )}
            </div>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/products/${product.id}/pdf`} target="_blank" rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold link-hover" data-testid="detail-pdf-btn" style={{ color: "var(--orange)" }}>
              <Download size={15} /> Télécharger la fiche produit (PDF)
            </a>
            {(product.rental_price != null || product.subcategory === "Exosquelettes & assistance physique") && (
              <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5" style={{ background: "var(--ink)", color: "var(--bone)" }} data-testid="rental-block">
                <div>
                  <span className="overline" style={{ color: "var(--orange)" }}>Location — testez avant d'acheter</span>
                  <p className="font-display font-bold text-lg mt-1">Loyer mensuel sur devis</p>
                  <p className="text-xs mt-1" style={{ color: "rgba(245,245,240,0.65)" }}>Sans engagement · loyer déduit en cas d'achat · étude de poste incluse</p>
                </div>
                <Link to={`/contact?produit=${encodeURIComponent(`Location — ${product.name}`)}`} className="px-6 py-3 font-semibold text-sm whitespace-nowrap text-center" style={{ background: "var(--orange)", color: "var(--bone)" }} data-testid="rental-request-btn">
                  Demander une location
                </Link>
              </div>
            )}
            {product.stock_qty !== 0 && (
              <p className="mt-4 text-sm flex items-center gap-2" style={{ color: "var(--ink-2)" }}>
                <ShieldCheck size={15} style={{ color: "var(--orange)" }} /> Devis gratuit et sans engagement · Livraison France & Afrique
              </p>
            )}
          </Reveal>
        </div>
      </section>

      {related.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-16" data-testid="related-products-section">
          <h2 className="font-display font-black text-3xl mb-8" style={{ borderTop: "1px solid var(--border)", paddingTop: "3rem" }}>Produits complémentaires</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
            {related.map((p) => (
              <Link key={p.id} to={`/catalogue/${p.id}`} className="group p-5 flex flex-col" style={{ background: "var(--white)" }} data-testid={`related-product-${p.id}`}>
                <div className="overflow-hidden mb-4">
                  <img src={p.image} alt={p.name} className="w-full h-40 object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <h3 className="font-display font-bold leading-tight flex-1">{p.name}</h3>
                <p className="mt-3 font-display font-black text-lg" style={{ color: "var(--orange)" }}>Sur devis</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24" data-testid="reviews-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" style={{ borderTop: "1px solid var(--border)", paddingTop: "3rem" }}>
          <div className="lg:col-span-7">
            <h2 className="font-display font-black text-3xl mb-2">Avis clients</h2>
            <div className="flex items-center gap-3 mb-8">
              {product.rating_count > 0 ? (
                <>
                  <Stars value={product.rating_avg} size={20} />
                  <span style={{ color: "var(--ink-2)" }}>{product.rating_avg.toFixed(1)} / 5 · {product.rating_count} avis</span>
                </>
              ) : (
                <span style={{ color: "var(--ink-2)" }}>Aucun avis pour le moment. Soyez le premier !</span>
              )}
            </div>
            <div className="space-y-px" style={{ background: reviews.length ? "var(--border)" : "transparent", border: reviews.length ? "1px solid var(--border)" : "none" }}>
              {reviews.map((r) => (
                <div key={r.id} className="p-5" style={{ background: "var(--white)" }} data-testid={`review-${r.id}`}>
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <span className="font-semibold flex items-center gap-2">
                      {r.name}
                      {r.verified && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5" style={{ background: "rgba(62,142,90,0.12)", color: "#3E8E5A" }} data-testid={`verified-badge-${r.id}`}>
                          <ShieldCheck size={11} /> Achat vérifié
                        </span>
                      )}
                    </span>
                    <Stars value={r.rating} size={14} />
                  </div>
                  {r.comment && <p style={{ color: "var(--ink-2)" }}>{r.comment}</p>}
                  <p className="text-xs font-mono2 mt-2" style={{ color: "var(--ink-2)" }}>{(r.created_at || "").slice(0, 10)}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <form onSubmit={submitReview} className="p-6 md:p-8" style={{ background: "var(--white)", border: "1px solid var(--border)" }} data-testid="review-form">
              <h3 className="font-display font-bold text-xl mb-5">Laisser un avis</h3>
              <div className="mb-4">
                <p className="overline mb-2" style={{ color: "var(--ink-2)" }}>Votre note</p>
                <Stars value={rf.rating} size={28} interactive onChange={(n) => setRf({ ...rf, rating: n })} />
              </div>
              <input className="field mb-3" placeholder="Votre nom *" required value={rf.name} onChange={(e) => setRf({ ...rf, name: e.target.value })} data-testid="review-name" />
              <input className="field mb-3" type="email" placeholder="Email d'achat (optionnel — badge Achat vérifié)" value={rf.email || ""} onChange={(e) => setRf({ ...rf, email: e.target.value })} data-testid="review-email" />
              <textarea className="field mb-4" rows={4} placeholder="Votre commentaire" value={rf.comment} onChange={(e) => setRf({ ...rf, comment: e.target.value })} data-testid="review-comment" />
              <button type="submit" disabled={rSubmitting} className="btn-primary w-full py-3 font-semibold" data-testid="review-submit">
                {rSubmitting ? "Envoi…" : "Publier mon avis"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
